import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { money } from "@/lib/format";
import { TONE, valueNote } from "./emailTokens";

/** Four headline numbers for the week. */
const WeeklyTiles = ({ weekly }) => (
  <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
    {[
      [
        "Quotes out",
        weekly.quoted_count,
        valueNote(weekly.quoted_value, weekly.quoted_monthly_value),
      ],
      ["Won", weekly.won_count, valueNote(weekly.won_value, weekly.won_monthly_value)],
      ["Lost", weekly.lost_count, ""],
      ["Overdue", weekly.overdue_count, "left waiting"],
    ].map(([label, value, note]) => (
      <div
        key={label}
        className="rounded-lg border border-[color:var(--tt-teal-line)] bg-[color:var(--tt-teal-mist)] px-3 py-2"
      >
        <dt className="text-[9px] font-bold uppercase tracking-[0.1em] text-[color:var(--tt-teal-deep)]">
          {label}
        </dt>
        <dd className="mt-0.5 text-lg font-bold leading-tight text-foreground">{value}</dd>
        <dd className="text-[10px] text-muted-foreground">{note}</dd>
      </div>
    ))}
  </dl>
);

/** One recap group — quotes out, won, lost, still waiting. */
const WeeklyGroup = ({ group }) => (
  <div
    data-testid={`weekly-preview-${group.key}`}
    className="overflow-hidden rounded-lg border border-border"
  >
    <div
      className={`border-b px-3.5 py-2 ${
        group.tone === "warn"
          ? TONE.warn
          : "border-[color:var(--tt-teal-line)] bg-[color:var(--tt-teal-mist)] text-[color:var(--tt-teal-deep)]"
      }`}
    >
      <p className="text-[11px] font-bold uppercase tracking-[0.08em]">
        {group.title} · {group.count}
      </p>
      <p className="mt-0.5 text-[11px] leading-snug opacity-90">{group.blurb}</p>
    </div>
    <ul className="divide-y divide-border">
      {group.rows.map((row) => (
        <li
          key={row.quote_ref}
          className="flex items-start justify-between gap-3 px-3.5 py-2.5"
        >
          <span>
            <span className="block text-[13px] font-bold text-foreground">
              {row.company || row.full_name}
            </span>
            <span className="block text-[11px] text-muted-foreground">
              {row.service_label}
              {row.closed_reason_label ? ` · ${row.closed_reason_label}` : ""}
            </span>
          </span>
          <span className="whitespace-nowrap font-amount text-[13px] font-bold text-foreground">
            {money(row.quote_total)}
            {row.pricing_type === "monthly" ? (
              <span className="text-[10px] font-normal text-muted-foreground">/month</span>
            ) : null}
          </span>
        </li>
      ))}
      {group.more ? (
        <li className="px-3.5 py-2 text-[11px] text-muted-foreground">
          and {group.more} more on the board
        </li>
      ) : null}
    </ul>
  </div>
);

/** Anyone past the response promise with nobody having ticked them off. */
const OverdueGroup = ({ weekly, clock }) => (
  <div
    data-testid="weekly-preview-overdue"
    className="overflow-hidden rounded-lg border border-border"
  >
    <div className={`border-b px-3.5 py-2 ${TONE.warn}`}>
      <p className="text-[11px] font-bold uppercase tracking-[0.08em]">
        Left waiting too long · {weekly.overdue_count}
      </p>
      <p className="mt-0.5 text-[11px] leading-snug opacity-90">
        {clock.label}, and nobody has ticked these off.
      </p>
    </div>
    <ul className="divide-y divide-border">
      {weekly.overdue.map((person) => (
        <li key={person.reference} className="px-3.5 py-2.5">
          <p className="text-[13px] font-bold text-foreground">
            {person.company || person.full_name}
            <span className="ml-1.5 font-amount text-[11px] font-normal text-muted-foreground">
              {person.reference}
            </span>
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {person.route_label} · {person.phone_display} ·{" "}
            <span className="font-bold text-[color:var(--tt-warn-text)]">
              waiting {person.waited_label}
            </span>
          </p>
        </li>
      ))}
    </ul>
  </div>
);

export const WeeklyPreviewDialog = ({ preview, clock, onClose }) => {
  const weekly = preview?.weekly;
  return (
    <Dialog open={Boolean(preview)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        data-testid="weekly-preview-dialog"
        className="max-h-[85vh] overflow-y-auto bg-white sm:max-w-xl"
      >
        <DialogHeader className="text-left">
          <DialogTitle>{weekly?.subject}</DialogTitle>
          <DialogDescription>
            {weekly?.week_label}
            {weekly?.win_rate !== null && weekly?.win_rate !== undefined
              ? ` · win rate ${weekly.win_rate}%`
              : ""}
          </DialogDescription>
        </DialogHeader>

        {!weekly?.has_news ? (
          <p
            data-testid="weekly-preview-empty"
            className="rounded-lg border border-border bg-[color:var(--tt-surface-2)] px-4 py-6 text-center text-sm text-muted-foreground"
          >
            Nothing came in and nothing closed this week, and nobody is waiting on us.
          </p>
        ) : (
          <div className="space-y-3">
            <WeeklyTiles weekly={weekly} />
            {weekly.groups
              .filter((group) => group.count)
              .map((group) => (
                <WeeklyGroup key={group.key} group={group} />
              ))}
            {weekly.overdue_count ? <OverdueGroup weekly={weekly} clock={clock} /> : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
